import React, { useState, useMemo, useEffect, useRef } from 'react';
import { CalendarDays, ArrowRight, ArrowLeft, Copy, Check, Plus, X, Loader2, Search } from 'lucide-react';
import { caseService, projectService, taskService } from '../../services/api';

const FEDERAL_HOLIDAYS = [
  { name: "New Year's Day", month: 1, day: 1 },
  { name: "Martin Luther King Jr. Day", month: 1, weekday: 1, occurrence: 3 },
  { name: "Presidents' Day", month: 2, weekday: 1, occurrence: 3 },
  { name: "Memorial Day", month: 5, weekday: 1, occurrence: -1 },
  { name: "Juneteenth", month: 6, day: 19 },
  { name: "Independence Day", month: 7, day: 4 },
  { name: "Labor Day", month: 9, weekday: 1, occurrence: 1 },
  { name: "Columbus Day", month: 10, weekday: 1, occurrence: 2 },
  { name: "Veterans Day", month: 11, day: 11 },
  { name: "Thanksgiving Day", month: 11, weekday: 4, occurrence: 4 },
  { name: "Christmas Day", month: 12, day: 25 },
];

function getNthWeekdayOfMonth(year, month, weekday, occurrence) {
  if (occurrence === -1) {
    // Last occurrence
    const lastDay = new Date(year, month, 0).getDate();
    for (let d = lastDay; d >= 1; d--) {
      const date = new Date(year, month - 1, d);
      if (date.getDay() === weekday) return date;
    }
  }
  let count = 0;
  for (let d = 1; d <= 31; d++) {
    const date = new Date(year, month - 1, d);
    if (date.getMonth() !== month - 1) break;
    if (date.getDay() === weekday) {
      count++;
      if (count === occurrence) return date;
    }
  }
  return null;
}

function getObservedHoliday(date) {
  const day = date.getDay();
  if (day === 0) return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
  if (day === 6) return new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1);
  return date;
}

function getHolidaysForYear(year) {
  const holidays = new Set();
  for (const h of FEDERAL_HOLIDAYS) {
    let date;
    if (h.day) {
      date = new Date(year, h.month - 1, h.day);
    } else {
      date = getNthWeekdayOfMonth(year, h.month, h.weekday, h.occurrence);
    }
    if (date) {
      const observed = getObservedHoliday(date);
      holidays.add(observed.toISOString().split('T')[0]);
    }
  }
  return holidays;
}

function isBusinessDay(date, holidays) {
  const day = date.getDay();
  if (day === 0 || day === 6) return false;
  if (holidays && holidays.has(date.toISOString().split('T')[0])) return false;
  return true;
}

function addDays(startDate, numDays, direction, dayType, excludeHolidays) {
  const start = new Date(startDate + 'T00:00:00');
  if (isNaN(start.getTime())) return null;

  if (dayType === 'calendar') {
    const result = new Date(start);
    result.setDate(result.getDate() + (direction === 'after' ? numDays : -numDays));
    return result;
  }

  // Business days
  const years = new Set();
  // Pre-compute holidays for a range of years
  const startYear = start.getFullYear();
  for (let y = startYear - 1; y <= startYear + Math.ceil(numDays / 200) + 1; y++) {
    years.add(y);
  }
  let holidays = null;
  if (excludeHolidays) {
    holidays = new Set();
    for (const y of years) {
      for (const h of getHolidaysForYear(y)) {
        holidays.add(h);
      }
    }
  }

  const result = new Date(start);
  const step = direction === 'after' ? 1 : -1;
  let remaining = numDays;
  while (remaining > 0) {
    result.setDate(result.getDate() + step);
    if (isBusinessDay(result, holidays)) {
      remaining--;
    }
  }
  return result;
}

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function formatDate(date) {
  return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

const DateCalculator = () => {
  const today = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(today);
  const [numDays, setNumDays] = useState('');
  const [direction, setDirection] = useState('after');
  const [dayType, setDayType] = useState('calendar');
  const [excludeHolidays, setExcludeHolidays] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [cases, setCases] = useState([]);
  const [projects, setProjects] = useState([]);
  const [taskTitle, setTaskTitle] = useState('');
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [loadingCases, setLoadingCases] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [taskSuccess, setTaskSuccess] = useState(false);
  const [taskError, setTaskError] = useState('');
  const [caseSearch, setCaseSearch] = useState('');
  const [caseDropdownOpen, setCaseDropdownOpen] = useState(false);
  const caseDropdownRef = useRef(null);

  // Close case dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (caseDropdownRef.current && !caseDropdownRef.current.contains(e.target)) {
        setCaseDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load active cases when task form opens, pre-select from localStorage
  useEffect(() => {
    if (!showTaskForm) return;
    setLoadingCases(true);
    caseService.getCases({ status_filter: 'active' })
      .then(res => {
        const data = res.data || res;
        setCases(data);

        // Pre-select from localStorage
        try {
          const stored = JSON.parse(localStorage.getItem('lastViewedCase'));
          if (stored?.caseId && data.some(c => c.id === stored.caseId)) {
            setSelectedCaseId(stored.caseId);
          }
        } catch { /* ignore */ }
      })
      .catch(() => setCases([]))
      .finally(() => setLoadingCases(false));
  }, [showTaskForm]);

  // Load projects when case is selected, pre-select from localStorage
  useEffect(() => {
    if (!selectedCaseId) { setProjects([]); setSelectedProjectId(''); return; }
    setLoadingProjects(true);
    setSelectedProjectId('');
    projectService.getProjectsByCase(selectedCaseId)
      .then(res => {
        const data = res.data || res;
        setProjects(data);

        // Pre-select from localStorage if same case
        try {
          const storedCase = JSON.parse(localStorage.getItem('lastViewedCase'));
          const storedProject = JSON.parse(localStorage.getItem('lastViewedProject'));
          if (storedCase?.caseId === selectedCaseId && storedProject?.projectId && data.some(p => p.id === storedProject.projectId)) {
            setSelectedProjectId(storedProject.projectId);
          }
        } catch { /* ignore */ }
      })
      .catch(() => setProjects([]))
      .finally(() => setLoadingProjects(false));
  }, [selectedCaseId]);

  // Sort cases: last viewed first, then alphabetical
  const sortedFilteredCases = useMemo(() => {
    let lastViewedCaseId = null;
    try {
      lastViewedCaseId = JSON.parse(localStorage.getItem('lastViewedCase'))?.caseId;
    } catch { /* ignore */ }

    const filtered = cases.filter(c => {
      if (!caseSearch.trim()) return true;
      const search = caseSearch.toLowerCase();
      return (c.title && c.title.toLowerCase().includes(search)) ||
        (c.case_number && c.case_number.toLowerCase().includes(search));
    });

    return filtered.sort((a, b) => {
      if (a.id === lastViewedCaseId) return -1;
      if (b.id === lastViewedCaseId) return 1;
      return (a.title || '').localeCompare(b.title || '');
    });
  }, [cases, caseSearch]);

  // Sort projects: last viewed first
  const sortedProjects = useMemo(() => {
    let lastViewedProjectId = null;
    try {
      lastViewedProjectId = JSON.parse(localStorage.getItem('lastViewedProject'))?.projectId;
    } catch { /* ignore */ }

    return [...projects].sort((a, b) => {
      if (a.id === lastViewedProjectId) return -1;
      if (b.id === lastViewedProjectId) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [projects]);

  const selectedCase = cases.find(c => c.id === selectedCaseId);

  const handleCreateTask = async () => {
    if (!selectedProjectId || !taskTitle.trim()) return;
    setCreatingTask(true);
    setTaskError('');
    try {
      const dueDate = result.toISOString();
      await taskService.createTask({
        project_id: selectedProjectId,
        title: taskTitle.trim(),
        due_date: dueDate,
        status: 'todo',
        priority: 'medium',
      });
      setTaskSuccess(true);
      setTimeout(() => {
        setShowTaskForm(false);
        setTaskSuccess(false);
        setTaskTitle('');
        setSelectedCaseId('');
        setSelectedProjectId('');
        setCaseSearch('');
      }, 1500);
    } catch {
      setTaskError('Failed to create task. Please try again.');
    } finally {
      setCreatingTask(false);
    }
  };

  const result = useMemo(() => {
    if (!startDate || !numDays || numDays <= 0) return null;
    return addDays(startDate, parseInt(numDays), direction, dayType, excludeHolidays);
  }, [startDate, numDays, direction, dayType, excludeHolidays]);

  const resultText = result ? `${DAYS_OF_WEEK[result.getDay()]}, ${formatDate(result)}` : null;

  const handleCopy = () => {
    if (resultText) {
      navigator.clipboard.writeText(resultText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Number of Days</label>
          <input
            type="number"
            min="1"
            value={numDays}
            onChange={(e) => setNumDays(e.target.value)}
            placeholder="e.g. 14"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Direction</label>
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              onClick={() => setDirection('before')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                direction === 'before'
                  ? 'bg-stone-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <ArrowLeft className="h-4 w-4" />
              Before
            </button>
            <button
              onClick={() => setDirection('after')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                direction === 'after'
                  ? 'bg-stone-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              After
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Day Type</label>
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              onClick={() => setDayType('calendar')}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                dayType === 'calendar'
                  ? 'bg-stone-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Calendar Days
            </button>
            <button
              onClick={() => setDayType('business')}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                dayType === 'business'
                  ? 'bg-stone-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Business Days
            </button>
          </div>
        </div>
      </div>

      {dayType === 'business' && (
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={excludeHolidays}
            onChange={(e) => setExcludeHolidays(e.target.checked)}
            className="rounded border-gray-300 text-gray-800 focus:ring-gray-500"
          />
          Exclude federal holidays
        </label>
      )}

      {result && (
        <>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">
                  {numDays} {dayType === 'business' ? 'business' : 'calendar'} day{numDays !== '1' ? 's' : ''} {direction} {formatDate(new Date(startDate + 'T00:00:00'))}
                </p>
                <p className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <CalendarDays className="h-6 w-6 text-yellow-600" />
                  {resultText}
                </p>
              </div>
              <button
                onClick={handleCopy}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Copy date"
              >
                {copied ? <Check className="h-5 w-5 text-green-500" /> : <Copy className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Create task button */}
          {!showTaskForm && !taskSuccess && (
            <button
              onClick={() => setShowTaskForm(true)}
              className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-800 font-medium"
            >
              <Plus className="h-4 w-4" />
              Create a task with this date
            </button>
          )}

          {/* Task creation form */}
          {showTaskForm && !taskSuccess && (
            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-700">Create Task</p>
                <button
                  onClick={() => { setShowTaskForm(false); setTaskError(''); }}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Task Title</label>
                <input
                  type="text"
                  value={taskTitle}
                  onChange={(e) => setTaskTitle(e.target.value)}
                  placeholder="e.g. File motion response"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Case (active only)</label>
                {loadingCases ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading cases...
                  </div>
                ) : (
                  <div className="relative" ref={caseDropdownRef}>
                    {selectedCaseId && !caseDropdownOpen ? (
                      <button
                        type="button"
                        onClick={() => { setCaseDropdownOpen(true); setCaseSearch(''); }}
                        className="w-full px-3 py-2 text-sm text-left border border-gray-300 rounded-lg hover:border-gray-400 flex items-center justify-between"
                      >
                        <span className="truncate">
                          {selectedCase?.case_number ? `${selectedCase.case_number} - ` : ''}{selectedCase?.title}
                        </span>
                        <X
                          className="h-4 w-4 text-gray-400 hover:text-gray-600 flex-shrink-0 ml-2"
                          onClick={(e) => { e.stopPropagation(); setSelectedCaseId(''); setSelectedProjectId(''); }}
                        />
                      </button>
                    ) : (
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          value={caseSearch}
                          onChange={(e) => { setCaseSearch(e.target.value); setCaseDropdownOpen(true); }}
                          onFocus={() => setCaseDropdownOpen(true)}
                          placeholder="Search cases..."
                          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
                          autoFocus={caseDropdownOpen}
                        />
                      </div>
                    )}
                    {caseDropdownOpen && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {sortedFilteredCases.length === 0 ? (
                          <p className="px-3 py-2 text-sm text-gray-500">No cases found</p>
                        ) : sortedFilteredCases.map((c) => {
                          let lastViewedCaseId = null;
                          try { lastViewedCaseId = JSON.parse(localStorage.getItem('lastViewedCase'))?.caseId; } catch { /* ignore */ }
                          const isRecent = c.id === lastViewedCaseId;
                          return (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => { setSelectedCaseId(c.id); setCaseDropdownOpen(false); setCaseSearch(''); }}
                              className={`w-full px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors flex items-center justify-between ${
                                c.id === selectedCaseId ? 'bg-gray-100 font-medium' : ''
                              }`}
                            >
                              <span className="truncate">{c.case_number ? `${c.case_number} - ` : ''}{c.title}</span>
                              {isRecent && <span className="text-xs text-gray-400 flex-shrink-0 ml-2">Recent</span>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {selectedCaseId && (
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Project</label>
                  {loadingProjects ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading projects...
                    </div>
                  ) : projects.length === 0 ? (
                    <p className="text-sm text-gray-500 py-2">No projects found for this case.</p>
                  ) : (
                    <select
                      value={selectedProjectId}
                      onChange={(e) => setSelectedProjectId(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400"
                    >
                      <option value="">Select a project...</option>
                      {sortedProjects.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {taskError && (
                <p className="text-sm text-red-600">{taskError}</p>
              )}

              <button
                onClick={handleCreateTask}
                disabled={!selectedProjectId || !taskTitle.trim() || creatingTask}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-yellow-500 rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {creatingTask ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Creating...</>
                ) : (
                  <><Plus className="h-4 w-4" /> Create Task</>
                )}
              </button>
            </div>
          )}

          {/* Success message */}
          {taskSuccess && (
            <div className="flex items-center gap-2 text-sm text-green-700 font-medium">
              <Check className="h-4 w-4" />
              Task created successfully!
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DateCalculator;
